'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';

interface SignUpFields {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
  terms: boolean;
}

interface Props {
  onSuccess: () => void;
}

export default function SignUpForm({ onSuccess }: Props) {
  const router = useRouter();
  const { signUp } = useAuth();
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm<SignUpFields>();

  const password = watch('password');

  const onSubmit = async (data: SignUpFields) => {
    setLoading(true);
    try {
      await signUp(data.email, data.password, { fullName: data.fullName });
      toast.success('Account created! Let\'s set up your organization.');
      router.push('/organization-onboarding');
    } catch (err: any) {
      setLoading(false);
      setError('email', {
        message: err?.message || 'Failed to create account. Please try again.',
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      {/* Google */}
      <button
        type="button"
        className="w-full flex items-center justify-center gap-3 py-2.5 px-4 rounded-xl border border-border bg-secondary hover:bg-secondary/80 text-sm font-medium text-foreground transition-all duration-150 active:scale-[0.98]"
      >
        <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        Continue with Google
      </button>

      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground">or create with email</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      {/* Full name */}
      <div>
        <label htmlFor="signup-name" className="block text-xs font-medium text-muted-foreground mb-1.5">Full name</label>
        <input
          id="signup-name"
          type="text"
          autoComplete="name"
          placeholder="Marcus Webb"
          {...register('fullName', { required: 'Full name is required' })}
          className={`w-full px-3 py-2.5 bg-secondary border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all ${errors.fullName ? 'border-red-500/50' : 'border-border'}`}
        />
        {errors.fullName && <p className="mt-1.5 text-xs text-red-400">{errors.fullName.message}</p>}
      </div>

      {/* Email */}
      <div>
        <label htmlFor="signup-email" className="block text-xs font-medium text-muted-foreground mb-1.5">Work email</label>
        <input
          id="signup-email"
          type="email"
          autoComplete="email"
          placeholder="you@company.com"
          {...register('email', {
            required: 'Email is required',
            pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' },
          })}
          className={`w-full px-3 py-2.5 bg-secondary border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all ${errors.email ? 'border-red-500/50' : 'border-border'}`}
        />
        {errors.email && <p className="mt-1.5 text-xs text-red-400">{errors.email.message}</p>}
      </div>

      {/* Password */}
      <div>
        <label htmlFor="signup-password" className="block text-xs font-medium text-muted-foreground mb-1.5">Password</label>
        <div className="relative">
          <input
            id="signup-password"
            type={showPw ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder="Min 8 characters"
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 8, message: 'Must be at least 8 characters' },
            })}
            className={`w-full px-3 py-2.5 pr-10 bg-secondary border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all ${errors.password ? 'border-red-500/50' : 'border-border'}`}
          />
          <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors" aria-label="Toggle password visibility">
            <Icon name={showPw ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
          </button>
        </div>
        {errors.password && <p className="mt-1.5 text-xs text-red-400">{errors.password.message}</p>}
      </div>

      {/* Confirm password */}
      <div>
        <label htmlFor="signup-confirm" className="block text-xs font-medium text-muted-foreground mb-1.5">Confirm password</label>
        <input
          id="signup-confirm"
          type="password"
          autoComplete="new-password"
          placeholder="••••••••"
          {...register('confirmPassword', {
            required: 'Please confirm your password',
            validate: (v) => v === password || 'Passwords do not match',
          })}
          className={`w-full px-3 py-2.5 bg-secondary border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all ${errors.confirmPassword ? 'border-red-500/50' : 'border-border'}`}
        />
        {errors.confirmPassword && <p className="mt-1.5 text-xs text-red-400">{errors.confirmPassword.message}</p>}
      </div>

      {/* Terms */}
      <div>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            {...register('terms', { required: 'You must accept the terms' })}
            className="w-4 h-4 mt-0.5 rounded border-border bg-secondary accent-blue-500 cursor-pointer shrink-0"
          />
          <span className="text-xs text-muted-foreground leading-relaxed">
            I agree to the{' '}
            <a href="#" className="text-blue-400 hover:underline">Terms of Service</a>
            {' '}and{' '}
            <a href="#" className="text-blue-400 hover:underline">Privacy Policy</a>
          </span>
        </label>
        {errors.terms && <p className="mt-1 text-xs text-red-400">{errors.terms.message}</p>}
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white text-sm font-semibold transition-all duration-150 active:scale-[0.98] flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
            Creating account…
          </>
        ) : 'Create Account'}
      </button>
    </form>
  );
}